import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { getObjectScreenNameFromPath } from '@/design-system/components/navigatorEmbed/UtxAppNavigatorEmbed';
import { UniTabHeader } from '@/design-system/components/tabs';
import { H6, TextCaption } from '@/design-system/components/typography';
import { AttachmentViewer } from '@/features/bill-payment/screens/components/attachmentViewer/AttachmentViewer';
import { DateFormat } from '@/utils';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { iframePostMessage } from '@/utils/iframePostMessage';
import { StackActions, useFocusEffect, useNavigation } from '@react-navigation/native';
import { Pressable, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import _, { toNumber } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Platform, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackRoutes } from 'types';
import BillPaymentApp from '../..';
import { IntervalBillPayment, ScheduleBillPayment } from '../../enum';
import { PAYMENT_TYPE, useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentStackRoutes, BillPaymentSuccessProp } from '../../payment.navigator';
import { displayAmountWithCurrency, mapUrlLink, openImage64, openURL } from '../../utils/utils';
import { BillPaymentInfoItem } from '../components/BillPaymentInfoItem';
import { HeaderBackgroundImageComponent } from '../components/HeaderBackgroundImageComponent';

export const BillPaymentSuccessMobileComponent: React.FC<BillPaymentSuccessProp> = ({
  response,
  submitResponse: { refId },
  debitAccount,
  documentSelected,
  paymentType,
  userFee
}) => {
  const navigation = useNavigation<any>();
  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const { theme } = useDripsyTheme();
  const { width } = useWindowDimensions();
  const { $3, $17 } = theme.space || ({} as any);
  const widthScaleImage = width - ($3 as number) * 2;
  const heightScaleImage = (widthScaleImage / 343) * 250;
  const {
    general: { getThumbnailUrLFromResponse }
  } = useCreateBillPayment();
  const { t } = useTranslation('global-bill-payment');

  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  const insets = useSafeAreaInsets();

  const onReturn = () => {
    navigation.dispatch(
      StackActions.replace(RootStackRoutes.BillPayment, {
        screen: BillPaymentStackRoutes.Landing
      })
    );
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') {
        const onPopState = (event: any) => {
          window.history.pushState(event.state, '');
          setTimeout(() => {
            if (!document.location.href) return;
            const { parentName, subName } = mapUrlLink(document.location.href);
            var screenNameObject = getObjectScreenNameFromPath([BillPaymentApp], `/${subName}`);
            if (
              screenNameObject.screenName &&
              screenNameObject.routeStackName === parentName &&
              screenNameObject.screenName !== BillPaymentStackRoutes.Landing
            ) {
              onReturn();
            }
          }, 200);
        };
        window.addEventListener('popstate', onPopState);
        return () => {
          window.removeEventListener('popstate', onPopState);
        };
      }
    }, [])
  );

  const renderScheduleDate = () => {
    if (_.isEmpty(response.attributes?.schedule)) {
      return <></>;
    }

    return (
      <>
        {response.attributes?.schedule &&
        response.attributes?.schedule.type?.toLowerCase() !== ScheduleBillPayment.RECURRING.toLowerCase() ? (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.scheduled-date')}
            description={
              !!response.attributes?.schedule?.endDate
                ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        ) : (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.date-of-first-payment')}
            description={
              !!response.attributes?.schedule?.startDate
                ? formatDate(response.attributes?.schedule?.startDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        )}
      </>
    );
  };

  const renderImage = () => {
    if (!_.isEmpty(getThumbnailUrLFromResponse(response))) {
      return (
        <Pressable onPress={async () => openURL(getThumbnailUrLFromResponse(response))}>
          <AttachmentViewer width={widthScaleImage} height={heightScaleImage} imageUrl={getThumbnailUrLFromResponse(response)} />
        </Pressable>
      );
    }
    if (
      documentSelected &&
      documentSelected.type === 'success' &&
      (documentSelected.mimeType == 'image/png' || documentSelected.mimeType == 'image/jpeg')
    ) {
      return (
        <Pressable onPress={() => openImage64(documentSelected.uri)}>
          <AttachmentViewer width={widthScaleImage} height={heightScaleImage} imageUrl={documentSelected.uri} />
        </Pressable>
      );
    }
    return <></>;
  };

  return (
    <View sx={sxStyles.container}>
      <View sx={{ ...sxStyles.header, ...{ pt: insets.top } }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('global-bill-payment::glossary.title--payment-instruction')}
          onBack={() => onReturn()}
          labelColor="$white"
          sx={sxStyles.tab}
          showBackButton={false}
        />
      </View>
      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.viewRow}>
            <View sx={sxStyles.viewInfo}>
              <View sx={sxStyles.viewImage}>
                <Icon name="success2" width={$17} height={$17} />
              </View>
              <H6 bold sx={sxStyles.textGlobal}>
                {paymentType == PAYMENT_TYPE.GLOBAL
                  ? t('global-bill-payment::summary.title--overseas-instruction-placed')
                  : t('global-bill-payment::summary.title--local-bill-payment-placed')}
              </H6>
              <TextCaption sx={sxStyles.textNote}>{t('global-bill-payment::summary.text--allow-two-three-days')}</TextCaption>

              <View sx={sxStyles.fullWidth} onLayout={onLayout}>
                <TextCaption semiBold={true} sx={sxStyles.textLabel}>
                  {t('global-bill-payment::glossary.merchant-bank-name')}
                </TextCaption>
                <H6 bold={true} sx={sxStyles.textValueBold}>
                  {response.attributes?.destination?.attributes?.recipient}
                </H6>

                <TextCaption semiBold={true} sx={sxStyles.textLabel}>
                  {t('global-bill-payment::glossary.bill-amount')}
                </TextCaption>
                <H6 bold={true} sx={sxStyles.textValueBold}>
                  {displayAmountWithCurrency(response.attributes?.amount?.toString() ?? '', response.attributes?.asset ?? 'HKD')}
                </H6>

                <TextCaption semiBold={true} sx={sxStyles.textLabel}>
                  {t('global-bill-payment::glossary.payment-upload-invoice')}
                </TextCaption>
                {_.isNumber(widthScaleImage) && _.isNumber(heightScaleImage) && <View sx={sxStyles.imageWrapper}>{renderImage()}</View>}
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.bill-bank-account-number')}
                  description={response.attributes?.destination?.attributes?.accountNumber ?? '-'}
                  titleSx={sxStyles.viewFlex1}
                  descriptionSx={sxStyles.viewFlex1}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.payment-additional-details')}
                  description={
                    _.isEmpty(response.attributes?.destination?.attributes?.paymentInfo)
                      ? '-'
                      : response.attributes?.destination?.attributes?.paymentInfo ?? ''
                  }
                  titleSx={sxStyles.viewFlex1}
                  descriptionSx={sxStyles.viewFlex1}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.debit-account')}
                  description={_.isEmpty(debitAccount) ? '-' : debitAccount}
                  titleSx={sxStyles.viewFlex1}
                  descriptionSx={sxStyles.viewFlex2}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.instruction-fee')}
                  description={`${displayAmountWithCurrency(userFee, response.attributes?.asset ?? 'HKD')}`}
                  descriptionSx={{ color: '$danger' }}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.debit-amount')}
                  description={displayAmountWithCurrency(
                    `${Math.abs(toNumber(response.attributes?.amount) + parseFloat(userFee))}`,
                    response.attributes?.asset ?? 'HKD'
                  )}
                  descriptionSx={{ color: '$danger' }}
                />
                {renderScheduleDate()}
                {!_.isEmpty(response.attributes?.schedule?.interval) && (
                  <BillPaymentInfoItem
                    title={t('global-bill-payment::glossary.frequency')}
                    description={
                      response.attributes?.schedule?.interval === IntervalBillPayment.DAILY
                        ? t('global-bill-payment::glossary.daily')
                        : response.attributes?.schedule?.interval !== IntervalBillPayment.WEEKLY
                        ? t('global-bill-payment::glossary.monthly')
                        : t('global-bill-payment::glossary.weekly')
                    }
                  />
                )}
                {response.attributes?.schedule &&
                  response.attributes?.schedule.type?.toLowerCase() === ScheduleBillPayment.RECURRING.toLowerCase() && (
                    <BillPaymentInfoItem
                      title={t('global-bill-payment::glossary.payment-end-date')}
                      description={
                        !!response.attributes?.schedule?.endDate
                          ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                          : ' - '
                      }
                    />
                  )}
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.remarks')}
                  description={_.isEmpty(response.remarks) ? '-' : response.remarks ?? '-'}
                  titleSx={sxStyles.viewFlex1}
                  descriptionSx={sxStyles.viewFlex2}
                />
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.payment-instruction-time')}
                  description={moment().format(DateFormat.DATE_FORMAT.STANDARD)}
                />
                <BillPaymentInfoItem title={t('global-bill-payment::glossary.payment-ref-id')} description={refId} />
                <View sx={sxStyles.viewRowButton}>
                  <View sx={{ flex: 131 }}>
                    <UniButton
                      variant={ButtonVariant.outline1}
                      sx={sxStyles.viewButton}
                      labelSx={sxStyles.textReturn}
                      label={t('global-bill-payment::glossary.return')}
                      onPress={() => onReturn()}
                    />
                  </View>
                  <View sx={{ flex: 16 }} />
                  <View sx={{ flex: 220 }}>
                    <UniButton
                      sx={sxStyles.viewButton}
                      label={t('global-bill-payment::general.check-instruction-history')}
                      onPress={() => iframePostMessage('moveToBillPaymentList', true)}
                    />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    width: '100%',
    height: '100%'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  body: {
    backgroundColor: '$backgroundPrimary',
    flex: 1,
    zIndex: 10,
    px: '$4',
    py: '$3'
  },
  viewRow: {
    flexDirection: 'row',
    height: '100%'
  },
  viewInfo: {
    alignItems: 'center',
    pt: '$4',
    flex: 1
  },
  viewImage: {
    justifyContent: 'center',
    pb: '$1'
  },
  textGlobal: {
    mt: '$1',
    mb: '$2',
    textAlign: 'center',
    width: '80%'
  },
  textNote: {
    mb: '$8',
    textAlign: 'center'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: '$7'
  },
  viewButton: {
    width: '100%',
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  header: {
    flexDirection: 'column'
  },
  imageWrapper: {
    mt: '$2',
    mb: '$5'
  },
  textLabel: {
    color: '#666666',
    mt: '$5'
  },
  textValueBold: {
    color: '$secondary',
    mt: '$1'
  },
  viewFlex1: {
    flex: 1
  },
  viewFlex2: {
    flex: 2
  },
  fullWidth: {
    width: '100%'
  }
};
